import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SkeletonLine } from '~/techstyle-shared/react-components';

import useFadeOutUnmount from '../../../hooks/useFadeOutUnmount';

const VideoContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 4px;
`;

const StyledVideo = styled.video<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

const StyledSkeletonLine = styled(SkeletonLine)<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

type FilterVideoProps = {
  alt?: string;
  isFetchingAssets?: boolean;
  isPlaying?: boolean;
  onVideoError?: () => void;
  syncEpochMs?: number;
  videoSrc?: string;
};

const FilterVideo = ({
  videoSrc,
  alt,
  isFetchingAssets = true,
  isPlaying = true,
  onVideoError,
  syncEpochMs,
}: FilterVideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastSyncedEpochMsRef = useRef<number | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const syncVideoToEpoch = useCallback(
    (videoElement: HTMLVideoElement) => {
      if (!syncEpochMs) {
        return;
      }

      if (lastSyncedEpochMsRef.current === syncEpochMs) {
        return;
      }

      const durationSeconds = videoElement.duration;

      if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        return;
      }

      const elapsedSeconds = (Date.now() - syncEpochMs) / 1000;
      const syncedCurrentTime =
        ((elapsedSeconds % durationSeconds) + durationSeconds) %
        durationSeconds;

      videoElement.currentTime = syncedCurrentTime;
      lastSyncedEpochMsRef.current = syncEpochMs;
    },
    [syncEpochMs]
  );

  useEffect(() => {
    setIsVideoLoaded(false);
    lastSyncedEpochMsRef.current = null;
  }, [videoSrc, isFetchingAssets]);

  const hasVideoSrc =
    typeof videoSrc === 'string' && videoSrc.trim().length > 0;
  const shouldRenderVideo = !isFetchingAssets && hasVideoSrc;
  const isSkeletonVisible = isFetchingAssets || !isVideoLoaded;

  const { isMounted: showSkeleton, onTransitionEnd: onSkeletonTransitionEnd } =
    useFadeOutUnmount(isSkeletonVisible);

  useEffect(() => {
    const currentVideo = videoRef.current;

    if (!currentVideo || !shouldRenderVideo) {
      return;
    }

    if (isPlaying) {
      syncVideoToEpoch(currentVideo);
      currentVideo.play().catch(() => {
        // Placeholder component: ignore autoplay interruptions.
      });
      return;
    }

    currentVideo.pause();
  }, [isPlaying, shouldRenderVideo, syncVideoToEpoch]);

  if (!hasVideoSrc) {
    return null;
  }

  const handleVideoError = () => {
    setIsVideoLoaded(false);
    if (onVideoError) {
      onVideoError();
    }
  };

  const handleLoadedData = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    setIsVideoLoaded(true);

    if (isPlaying) {
      const loadedVideoElement = event.currentTarget;

      syncVideoToEpoch(loadedVideoElement);
      loadedVideoElement.play().catch(() => {
        // Placeholder component: ignore autoplay interruptions.
      });
    }
  };

  return (
    <VideoContainer aria-busy={isFetchingAssets}>
      {showSkeleton && (
        <StyledSkeletonLine
          $isVisible={isSkeletonVisible}
          onTransitionEnd={onSkeletonTransitionEnd}
        />
      )}
      {shouldRenderVideo && (
        <StyledVideo
          ref={videoRef}
          $isVisible={isVideoLoaded}
          muted
          loop
          playsInline
          autoPlay={isPlaying}
          preload="metadata"
          aria-label={alt}
          onLoadedData={handleLoadedData}
          onError={handleVideoError}
          src={videoSrc}
        />
      )}
    </VideoContainer>
  );
};

FilterVideo.propTypes = {
  alt: PropTypes.string,
  isFetchingAssets: PropTypes.bool,
  isPlaying: PropTypes.bool,
  onVideoError: PropTypes.func,
  syncEpochMs: PropTypes.number,
  videoSrc: PropTypes.string,
};

export default FilterVideo;
